/* eslint-env browser */
// Driver documents › Letter records (wave 3, W2_Portal_Tables look): the
// fraud-audit log of every generated letter in the modern table, newest
// first, with Refresh at the top.
//
// Data, exactly as before: one read when the tab opens (fetchLetterRecords,
// a get, no live listener) and again only when the person presses Refresh.
// Read-only. CSV for admins, as before (now with the computed amount filled
// in and Addis Ababa 12-hour dates). TIN is hidden in demo mode.
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import ExitToAppRoundedIcon from '@mui/icons-material/ExitToAppRounded';
import { FirebaseContext } from 'common';
import { DataTable, PageHeader, StatusChip, calmCaps, formatMoney } from '../components/modern';
import { fetchLetterRecords } from '../utils/letterRecords';

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);

// The computed figure of a record (newer records keep the debit total).
const computedOf = (r) => (r.computedDebitTotal != null ? r.computedDebitTotal : r.computedEarnings != null ? r.computedEarnings : r.computedNet);

// Tab 3 of the Driver Documents centre: the fraud-audit log of every generated
// letter. One-shot read on mount (no live listener) + manual refresh.
export default function LetterRecords() {
  const { t, i18n } = useTranslation();
  const firebase = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const label = (key, fallback, opts) => t(key, { defaultValue: fallback, ...(opts || {}) });

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const records = await fetchLetterRecords(firebase);
      setData(records);
    } catch (e) {
      console.error('Failed to load letter records:', e);
      setData([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [firebase]);

  useEffect(() => {
    load();
  }, [load]);

  const allowPrivate = !!(settings && settings.AllowCriticalEditsAdmin);
  const hidden = t('hidden_demo');
  const money = (v) => formatMoney(v, settings, true);
  const typeLabel = (r) => (r.type === 'clearance' ? label('clearance_letter', 'Clearance') : label('verification_letter', 'Verification'));
  const generatedBy = (r) => r.generatedByName || r.generatedByEmail || r.generatedByUid || '';
  const overrideText = (r) =>
    r.amountOverridden ? `${label('overridden', 'Overridden')} (${r.amountDeviation > 0 ? '+' : ''}${r.amountDeviation})` : r.type === 'clearance' ? label('ok', 'OK') : '';

  const columns = [
    { id: 'generatedAt', title: label('date', 'Date'), field: 'generatedAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'type',
      title: label('type', 'Type'),
      field: 'type',
      searchValue: typeLabel,
      sortValue: typeLabel,
      render: (r) => (
        <StatusChip
          tone={r.type === 'clearance' ? 'success' : 'info'}
          icon={r.type === 'clearance' ? <ExitToAppRoundedIcon /> : <VerifiedOutlinedIcon />}
          label={typeLabel(r)}
        />
      ),
    },
    { id: 'driverName', title: label('driver_name', 'Driver'), field: 'driverName', render: (r) => r.driverName || '-' },
    { id: 'plate', title: label('plate_number', 'Plate'), field: 'plate', nowrap: true, render: (r) => r.plate || '-' },
    {
      id: 'tin',
      title: t('tin'),
      field: 'tin',
      searchValue: (r) => (allowPrivate ? r.tin || '' : ''),
      sortValue: (r) => (allowPrivate ? r.tin || '' : ''),
      render: (r) => (allowPrivate ? r.tin || '-' : hidden),
    },
    { id: 'refNo', title: label('ref_no', 'Ref No'), field: 'refNo', nowrap: true, render: (r) => r.refNo || '-' },
    { id: 'language', title: label('language', 'Lang'), field: 'language', render: (r) => (r.language ? r.language.toUpperCase() : '-') },
    { id: 'amount', title: label('amount_earned', 'Amount'), field: 'amount', type: 'money', render: (r) => (r.amount ? money(r.amount) : '-') },
    {
      id: 'computed',
      title: label('computed_net', 'Computed'),
      type: 'money',
      sortValue: (r) => {
        const v = computedOf(r);
        return v != null ? Number(v) : null;
      },
      render: (r) => {
        const v = computedOf(r);
        return v != null ? money(v) : '-';
      },
    },
    {
      // Fraud flag — overridden amounts stand out with their deviation.
      id: 'override',
      title: label('override', 'Override'),
      field: 'amountOverridden',
      searchValue: overrideText,
      sortValue: (r) => (r.amountOverridden ? 2 : r.type === 'clearance' ? 1 : 0),
      render: (r) =>
        r.amountOverridden ? (
          <StatusChip tone="warning" icon={<WarningAmberRoundedIcon />} label={overrideText(r)} />
        ) : r.type === 'clearance' ? (
          <StatusChip tone="success" icon={<CheckRoundedIcon />} label={label('ok', 'OK')} />
        ) : (
          '-'
        ),
    },
    {
      id: 'overrideJustification',
      title: label('override_justification', 'Justification'),
      field: 'overrideJustification',
      render: (r) =>
        r.overrideJustification ? (
          <Tooltip title={r.overrideJustification}>
            <Box component="span" tabIndex={0} sx={{ maxWidth: 220, display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {r.overrideJustification}
            </Box>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    { id: 'subCity', title: label('sub_city', 'Sub City'), field: 'subCity', render: (r) => r.subCity || '-' },
    { id: 'generatedBy', title: label('generated_by', 'Generated By'), field: 'generatedByName', searchValue: generatedBy, sortValue: generatedBy, render: (r) => generatedBy(r) || '-' },
  ];

  // The CSV columns as before (raw values), with the computed amount filled
  // in for every record and the date in Addis Ababa 12-hour time.
  const exportColumns = [
    { title: label('date', 'Date'), field: 'generatedAt', type: 'datetime' },
    { title: label('type', 'Type'), field: 'type' },
    { title: label('driver_name', 'Driver'), field: 'driverName' },
    { title: label('plate_number', 'Plate'), field: 'plate' },
    { title: t('tin'), value: (r) => (allowPrivate ? r.tin || '' : hidden) },
    { title: label('ref_no', 'Ref No'), field: 'refNo' },
    { title: label('language', 'Lang'), field: 'language' },
    { title: label('amount_earned', 'Amount'), field: 'amount' },
    { title: label('computed_net', 'Computed'), value: (r) => computedOf(r) },
    { title: label('override', 'Override'), field: 'amountOverridden' },
    { title: label('override_justification', 'Justification'), field: 'overrideJustification' },
    { title: label('sub_city', 'Sub City'), field: 'subCity' },
    { title: label('generated_by', 'Generated By'), field: 'generatedByName' },
  ];

  const title = calmCaps(label('letter_records', 'Generated Letter Records'));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        secondaryActions={[
          { key: 'refresh', label: label('refresh', 'Refresh'), icon: <RefreshRoundedIcon />, onClick: load, disabled: loading },
        ]}
      />
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        loading={loading && !data.length}
        refreshing={loading && data.length > 0}
        error={failed ? { title: label('letter_records_load_failed', 'Could not load the letter records.'), onRetry: load } : null}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? label('loading', 'Loading…')
            : query
              ? label('table_count_shown', '{{shown}} of {{total}}', { shown: fmt(shown), total: fmt(total) })
              : t('letter_records_count', {
                  count: total,
                  n: fmt(total),
                  defaultValue_one: '{{n}} letter',
                  defaultValue_other: '{{n}} letters',
                  defaultValue: '{{n}} letters',
                })
        }
        resortKey={allowPrivate ? 'private' : 'demo'}
        prefsKey="letter_records"
        exportCsv={auth.profile.usertype === 'admin'}
        exportColumns={exportColumns}
        exportFileName="letter_records"
        exportTitle={title}
        minWidth={1400}
        emptyState={({ query }) =>
          query || failed ? null : { icon: <HistoryEduOutlinedIcon />, title: label('letter_records_empty', 'No letters generated yet') }
        }
      />
    </Box>
  );
}
